/**
 * ActivateStep — Step 5 (final) of setup wizard.
 *
 * Shows a comprehensive summary of the product activation:
 * - Product details
 * - Readiness gaps (DRR-172 regulatory gap detection)
 * - Workflow/Task Group/Regulatory Stack tabs
 * - LLM guidance button
 * - Team configuration
 * - Funding sources
 * - Activation summary with counts
 *
 * Replaces the old ConfirmStep. Uses deterministic product composition
 * instead of LLM guessing.
 */

import { useMemo, useState } from 'react';
import { gql } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import {
  Stack,
  Group,
  Text,
  Paper,
  Badge,
  SimpleGrid,
  Divider,
  ThemeIcon,
  Timeline,
  Tabs,
  Button,
  Card,
  Collapse,
  Loader,
  Alert,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconRocket,
  IconPackage,
  IconUsers,
  IconCurrencyDollar,
  IconChecklist,
  IconSettings,
  IconStars,
  IconEye,
  IconScale,
  IconCheck,
  IconAlertTriangle,
  IconBrain,
  IconListCheck,
  IconSitemap,
} from '@tabler/icons-react';
import type { ProductDefinition } from './ProductSelectionStep';
import type { TeamRoleConfig } from './TeamRolesStep';
import type { Payer } from '../hooks/useComposition';

// ── LLM Guidance Mutation ──
const GENERATE_TASK_GUIDANCE = gql`
  mutation GenerateTaskGuidance($input: GenerateTaskGuidanceInput!, $userId: String!) {
    generateTaskGuidance(input: $input, userId: $userId) {
      guidance {
        guidanceId
        steps {
          stepNumber
          title
          description
        }
        tips
        warnings
        complianceNotes
      }
    }
  }
`;

export interface ReadinessGap {
  ruleId: string;
  ruleName: string;
  missingConfigs: {
    configType: string;
    description: string;
  }[];
}

export interface ActivateStepProps {
  product: ProductDefinition;
  acknowledgedRulesets: boolean;
  teamRoles: TeamRoleConfig[];
  payers: Payer[];
  /** DRR-172: Readiness gaps from regulatory gap detection */
  readinessGaps?: ReadinessGap[];
  /** Task group IDs activated by this product (if available) */
  activatesTaskGroups?: string[];
}

const PAYER_LABELS: Record<Payer, string> = {
  private_pay: 'Private Pay',
  medicaid_ew: 'Medicaid EW',
  medicaid_cadi: 'Medicaid CADI',
  medicaid_dd: 'Medicaid DD',
  medicare: 'Medicare',
  va: 'VA Benefits',
  commercial_insurance: 'Commercial Insurance',
};

function industryLabel(industry: string): string {
  const labels: Record<string, string> = {
    assisted_living: 'Assisted Living',
    home_care: 'Home Care',
    dental: 'Dental',
    outpatient_rehab: 'Outpatient Rehab',
  };
  return labels[industry] ?? industry.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function jurisdictionLabel(jurisdiction: string): string {
  const labels: Record<string, string> = {
    MN: 'Minnesota', CA: 'California', TX: 'Texas', NY: 'New York',
    FL: 'Florida', WI: 'Wisconsin',
  };
  return labels[jurisdiction] ?? jurisdiction;
}

export function ActivateStep({
  product,
  acknowledgedRulesets,
  teamRoles,
  payers,
  readinessGaps,
  activatesTaskGroups,
}: ActivateStepProps) {
  const configuredRoles = teamRoles.filter((r) => r.count > 0);
  const totalStaff = teamRoles.reduce((sum, r) => sum + r.count, 0);
  const taskGroups = activatesTaskGroups ?? [];

  // Readiness gap collapsible
  const [gapsOpened, { toggle: toggleGaps }] = useDisclosure(false);

  // LLM guidance state
  const [guidanceResponse, setGuidanceResponse] = useState<string | null>(null);
  const [executeGuidance, { loading: guidanceLoading }] = useMutation<{ generateTaskGuidance: { guidance: { guidanceId: string; steps: Array<{ stepNumber: number; title: string; description: string }>; tips: string[]; warnings: string[]; complianceNotes: string[] } } }>(GENERATE_TASK_GUIDANCE);

  const handleExplainFacility = async () => {
    const summaryContext = {
      productName: product.name,
      industry: product.industry,
      jurisdiction: product.jurisdiction,
      rulesets: product.requiredRulesets,
      workflows: product.activatesWorkflows,
      taskGroups,
      teamRoles: configuredRoles.map((r) => `${r.roleName} (${r.count})`),
    };

    try {
      const { data } = await executeGuidance({
        variables: {
          input: {
            taskId: `activation-summary-${product.productId}`,
            tenantId: 'current',
            taskContext: summaryContext,
            userRole: 'administrator',
          },
          userId: 'system',
        },
      });

      if (data?.generateTaskGuidance?.guidance) {
        const g = data.generateTaskGuidance.guidance;
        const parts: string[] = [];
        if (g.steps?.length > 0) {
          parts.push(g.steps.map((s: { stepNumber: number; title: string; description: string }) =>
            `${s.stepNumber}. ${s.title}: ${s.description}`
          ).join('\n'));
        }
        if (g.tips?.length > 0) {
          parts.push('\nTips:\n' + g.tips.map((t: string) => `- ${t}`).join('\n'));
        }
        if (g.complianceNotes?.length > 0) {
          parts.push('\nCompliance Notes:\n' + g.complianceNotes.map((n: string) => `- ${n}`).join('\n'));
        }
        setGuidanceResponse(parts.join('\n'));
      }
    } catch {
      setGuidanceResponse('Unable to generate guidance at this time. The LLM agent service may be unavailable.');
    }
  };

  // Calculate total configs that will be provisioned
  const totals = useMemo(() => ({
    rulesets: product.requiredRulesets.length,
    workflows: product.activatesWorkflows.length,
    taskGroups: taskGroups.length,
    settings: product.activatesSettings.length,
    overrides: product.activatesOverrides.length,
    constellations: product.activatesConstellations.length,
    lenses: product.onboardingLenses.length,
    subgraphs: product.subgraphs.length,
    roles: configuredRoles.length,
    staff: totalStaff,
    payers: payers.length,
  }), [product, taskGroups, configuredRoles, totalStaff, payers]);

  const totalConfigs = totals.rulesets + totals.workflows + totals.taskGroups + totals.settings + totals.overrides;
  const hasGaps = readinessGaps && readinessGaps.length > 0;

  return (
    <Stack gap="md">
      <Group gap="xs">
        <ThemeIcon variant="light" size="lg" color="green">
          <IconRocket size={20} />
        </ThemeIcon>
        <div>
          <Text fw={600} size="lg">Review & Activate</Text>
          <Text size="sm" c="dimmed">
            Review your configuration before activating the product.
          </Text>
        </div>
      </Group>

      {/* Product Summary Card */}
      <Paper p="md" withBorder bg="blue.0" radius="md">
        <Group gap="xs" mb="sm">
          <IconPackage size={18} color="var(--mantine-color-blue-7)" />
          <Text fw={600} c="blue.8">{product.name}</Text>
          <Badge size="xs" color="blue" variant="light">v{product.version}</Badge>
        </Group>
        <Group gap="xs">
          <Badge size="sm" variant="outline" color="blue">
            {industryLabel(product.industry)}
          </Badge>
          <Badge size="sm" variant="outline" color="grape">
            {jurisdictionLabel(product.jurisdiction)}
          </Badge>
          <Badge size="sm" variant="light" color="gray">
            {product.subgraphs.join(', ')}
          </Badge>
        </Group>
      </Paper>

      {/* DRR-172: Regulatory Readiness Section */}
      {hasGaps && (
        <Alert
          icon={<IconAlertTriangle size={18} />}
          color="yellow"
          title={`${readinessGaps.length} regulatory readiness gap${readinessGaps.length !== 1 ? 's' : ''} detected`}
          radius="md"
        >
          <Text size="sm" mb="xs">
            These regulatory requirements do not have matching task groups or workflows yet.
            Your architect can generate them via the Proposal Review page.
          </Text>
          <Button
            variant="subtle"
            color="yellow"
            size="xs"
            onClick={toggleGaps}
          >
            {gapsOpened ? 'Hide details' : 'Show details'}
          </Button>
          <Collapse in={gapsOpened}>
            <Stack gap="xs" mt="xs">
              {readinessGaps.map((gap) => (
                <Paper key={gap.ruleId} p="xs" withBorder>
                  <Group gap="xs" mb={4}>
                    <Text size="sm" fw={500}>{gap.ruleName}</Text>
                    <Badge size="xs" variant="outline" color="gray">{gap.ruleId}</Badge>
                  </Group>
                  {gap.missingConfigs.map((mc, idx) => (
                    <Text key={idx} size="xs" c="dimmed">
                      Missing {mc.configType}: {mc.description}
                    </Text>
                  ))}
                </Paper>
              ))}
            </Stack>
          </Collapse>
        </Alert>
      )}

      {/* Configuration Summary Grid */}
      <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }}>
        <SummaryCard
          icon={<IconScale size={18} />}
          color="red"
          label="RuleSets"
          value={totals.rulesets}
          items={product.requiredRulesets}
        />
        <SummaryCard
          icon={<IconChecklist size={18} />}
          color="blue"
          label="Workflows"
          value={totals.workflows}
          items={product.activatesWorkflows}
        />
        <SummaryCard
          icon={<IconUsers size={18} />}
          color="violet"
          label="Team Roles"
          value={`${totals.roles} roles, ${totals.staff} staff`}
          items={configuredRoles.map((r) => `${r.roleName} (${r.count})`)}
        />
        <SummaryCard
          icon={<IconCurrencyDollar size={18} />}
          color="teal"
          label="Funding"
          value={totals.payers > 0 ? totals.payers : 'None'}
          items={payers.map((p) => PAYER_LABELS[p] ?? p)}
        />
      </SimpleGrid>

      <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }}>
        <SummaryCard
          icon={<IconSettings size={18} />}
          color="orange"
          label="Settings"
          value={totals.settings}
          items={product.activatesSettings}
        />
        <SummaryCard
          icon={<IconStars size={18} />}
          color="violet"
          label="Overrides"
          value={totals.overrides}
          items={product.activatesOverrides}
        />
        <SummaryCard
          icon={<IconStars size={18} />}
          color="grape"
          label="Constellations"
          value={totals.constellations}
          items={product.activatesConstellations}
        />
        <SummaryCard
          icon={<IconEye size={18} />}
          color="cyan"
          label="Lenses"
          value={totals.lenses}
          items={product.onboardingLenses}
        />
      </SimpleGrid>

      <Divider />

      {/* DRR-172: Product Includes — Tabbed Review */}
      <Text size="sm" fw={600}>What this product includes</Text>
      <Tabs defaultValue="workflows" variant="outline">
        <Tabs.List>
          <Tabs.Tab
            value="workflows"
            leftSection={<IconSitemap size={14} />}
          >
            Workflows ({totals.workflows})
          </Tabs.Tab>
          <Tabs.Tab
            value="taskgroups"
            leftSection={<IconListCheck size={14} />}
          >
            Task Groups ({totals.taskGroups})
          </Tabs.Tab>
          <Tabs.Tab
            value="regulatory"
            leftSection={<IconScale size={14} />}
          >
            Regulatory Stack ({totals.rulesets})
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="workflows" pt="sm">
          {product.activatesWorkflows.length === 0 ? (
            <Text size="sm" c="dimmed">No workflows included in this product.</Text>
          ) : (
            <SimpleGrid cols={{ base: 1, sm: 2 }}>
              {product.activatesWorkflows.map((wfId) => (
                <Paper key={wfId} p="sm" withBorder>
                  <Group gap="xs">
                    <IconSitemap size={14} color="var(--mantine-color-blue-6)" />
                    <Text size="sm" fw={500}>{wfId}</Text>
                  </Group>
                </Paper>
              ))}
            </SimpleGrid>
          )}
        </Tabs.Panel>

        <Tabs.Panel value="taskgroups" pt="sm">
          {taskGroups.length === 0 ? (
            <Text size="sm" c="dimmed">No task groups included in this product.</Text>
          ) : (
            <SimpleGrid cols={{ base: 1, sm: 2 }}>
              {taskGroups.map((tgId) => (
                <Paper key={tgId} p="sm" withBorder>
                  <Group gap="xs">
                    <IconListCheck size={14} color="var(--mantine-color-teal-6)" />
                    <Text size="sm" fw={500}>{tgId}</Text>
                  </Group>
                </Paper>
              ))}
            </SimpleGrid>
          )}
        </Tabs.Panel>

        <Tabs.Panel value="regulatory" pt="sm">
          {product.requiredRulesets.length === 0 ? (
            <Text size="sm" c="dimmed">No regulatory rulesets required by this product.</Text>
          ) : (
            <SimpleGrid cols={{ base: 1, sm: 2 }}>
              {product.requiredRulesets.map((rsId) => (
                <Paper key={rsId} p="sm" withBorder>
                  <Group gap="xs">
                    <IconScale size={14} color="var(--mantine-color-red-6)" />
                    <Text size="sm" fw={500}>{rsId}</Text>
                  </Group>
                </Paper>
              ))}
            </SimpleGrid>
          )}
        </Tabs.Panel>
      </Tabs>

      <Divider />

      {/* LLM Guidance Section */}
      <Group gap="xs">
        <Button
          variant="light"
          color="violet"
          leftSection={guidanceLoading ? <Loader size={16} /> : <IconBrain size={16} />}
          onClick={handleExplainFacility}
          loading={guidanceLoading}
        >
          Explain what this means for my facility
        </Button>
      </Group>

      {guidanceResponse && (
        <Card withBorder p="md" radius="md" bg="violet.0">
          <Text size="sm" fw={600} c="violet.8" mb="xs">
            AI Guidance
          </Text>
          <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
            {guidanceResponse}
          </Text>
        </Card>
      )}

      <Divider />

      {/* What Happens on Activate */}
      <Text size="sm" fw={600}>What happens when you activate</Text>
      <Timeline active={-1} bulletSize={24} lineWidth={2}>
        <Timeline.Item
          bullet={<Text size="xs" fw={700}>1</Text>}
          title={<Text size="sm" fw={500}>Provision {totals.subgraphs} infrastructure subgraph{totals.subgraphs !== 1 ? 's' : ''}</Text>}
        >
          <Text size="xs" c="dimmed">
            Subgraphs: {product.subgraphs.join(', ')}
          </Text>
        </Timeline.Item>
        <Timeline.Item
          bullet={<Text size="xs" fw={700}>2</Text>}
          title={<Text size="sm" fw={500}>Seed {totals.rulesets} regulatory ruleset{totals.rulesets !== 1 ? 's' : ''}</Text>}
        >
          <Text size="xs" c="dimmed">
            Legislation bundles with {product.requiredRulesets.length} statutory requirements.
            RuleSets define which rules and workflows are mandatory.
          </Text>
        </Timeline.Item>
        <Timeline.Item
          bullet={<Text size="xs" fw={700}>3</Text>}
          title={<Text size="sm" fw={500}>Activate {totals.workflows} workflow{totals.workflows !== 1 ? 's' : ''}</Text>}
        >
          <Text size="xs" c="dimmed">
            Operational workflows for admissions, care delivery, compliance, and reporting.
            Each workflow routes through safety-level approval.
          </Text>
        </Timeline.Item>
        <Timeline.Item
          bullet={<Text size="xs" fw={700}>4</Text>}
          title={<Text size="sm" fw={500}>Apply {totals.overrides + totals.settings} form customization{(totals.overrides + totals.settings) !== 1 ? 's' : ''}</Text>}
        >
          <Text size="xs" c="dimmed">
            Form schema overrides and settings specific to {industryLabel(product.industry)}.
            Fields, validation rules, and visibility conditions from backend.
          </Text>
        </Timeline.Item>
        <Timeline.Item
          bullet={<Text size="xs" fw={700}>5</Text>}
          title={<Text size="sm" fw={500}>Create team with {totals.roles} role{totals.roles !== 1 ? 's' : ''}</Text>}
        >
          <Text size="xs" c="dimmed">
            {configuredRoles.length > 0
              ? configuredRoles.map((r) => `${r.roleName} (${r.count})`).join(', ')
              : 'No roles configured yet — you can add team members after activation.'}
          </Text>
        </Timeline.Item>
      </Timeline>

      {/* Activation Total */}
      <Paper p="md" withBorder bg="green.0" radius="md">
        <Group justify="space-between">
          <Group gap="xs">
            <IconCheck size={18} color="var(--mantine-color-green-7)" />
            <Text fw={600} c="green.8">
              Ready to activate
            </Text>
          </Group>
          <Badge size="lg" color="green" variant="light">
            {totalConfigs} configs total
          </Badge>
        </Group>
        <Text size="xs" c="dimmed" mt="xs">
          Product status will transition from <Text span fw={500}>reference</Text> to <Text span fw={500}>activated</Text>.
          All configurations will be seeded to your tenant with write-once protection (DRR-087).
        </Text>
      </Paper>
    </Stack>
  );
}

function SummaryCard({
  icon,
  color,
  label,
  value,
  items,
}: {
  icon: React.ReactNode;
  color: string;
  label: string;
  value: number | string;
  items: string[];
}) {
  return (
    <Paper p="sm" withBorder>
      <Stack gap="xs">
        <Group gap="xs">
          <ThemeIcon size="sm" variant="light" color={color}>
            {icon}
          </ThemeIcon>
          <Text size="xs" fw={600}>
            {label}
          </Text>
        </Group>
        <Text size="lg" fw={700} c={color}>
          {value}
        </Text>
        {items.length > 0 && items.length <= 5 && (
          <Stack gap={2}>
            {items.map((item) => (
              <Text key={item} size="xs" c="dimmed" truncate>
                {item}
              </Text>
            ))}
          </Stack>
        )}
        {items.length > 5 && (
          <Text size="xs" c="dimmed">
            {items.slice(0, 3).join(', ')} + {items.length - 3} more
          </Text>
        )}
      </Stack>
    </Paper>
  );
}
